/**
 * Live chat (Tawk.to, loaded via GTM) → portal attribution.
 *
 * Tawk's transcript webhook only knows the visitor's external referrer. When a
 * chat starts we report its chat id with the same attribution the contact form
 * sends (UTMs, click ids, first/last touch, visitor id) and the page it was
 * started on. livechat-tawk joins the two when the transcript arrives. Signed-in
 * visitors are matched by their session JWT, which functions.invoke attaches.
 */

import { supabase } from "@/integrations/supabase/client";
import { getLeadAttribution } from "@/lib/attribution";

interface TawkApi {
  toggle?: () => void;
  maximize?: () => void;
  onChatStarted?: (data?: { chatId?: string }) => void;
}

declare global {
  interface Window {
    Tawk_API?: TawkApi;
  }
}

let installed = false;

export function initLiveChatTracking(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  // GTM's snippet does `var Tawk_API = Tawk_API || {}`, so whichever runs first,
  // both end up on the same object.
  const api: TawkApi = (window.Tawk_API = window.Tawk_API ?? {});
  const previous = api.onChatStarted;

  api.onChatStarted = (data) => {
    try { previous?.(data); } catch { /* another listener's problem */ }
    const chatId = data?.chatId;
    if (!chatId) return;
    try {
      const attribution = getLeadAttribution();
      void supabase.functions.invoke("livechat-tawk", {
        body: {
          chat_id: String(chatId),
          visitor_id: attribution.te_visitor_id,
          page_url: window.location.href,
          referrer: document.referrer || null,
          attribution,
        },
      }).catch(() => { /* attribution is best-effort; the chat itself is unaffected */ });
    } catch {
      // never break the chat widget
    }
  };
}
